import { A, useNavigate } from "@solidjs/router";
import { createSignal, For, onCleanup, type ParentProps } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";

const NAV = [
  { to: "/api-keys", label: "API keys" },
  { to: "/webhooks", label: "Webhooks" },
  { to: "/deliveries", label: "Deliveries" },
  { to: "/transactions", label: "Transactions" },
] as const;

/** Authenticated chrome: top bar with nav + sign-out, content below. On phones
 * the nav + account controls collapse behind a hamburger (below 768px). */
export function AppShell(props: ParentProps) {
  const { client, session, clearSession } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = createSignal(false);
  const close = () => setOpen(false);

  // The account id is long — clicking the header chip copies the full id.
  const [copied, setCopied] = createSignal(false);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;
  function copyAccountId() {
    const id = session()?.accountId;
    if (!id) return;
    void navigator.clipboard
      ?.writeText(id)
      .then(() => {
        setCopied(true);
        clearTimeout(copyTimer);
        copyTimer = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }
  onCleanup(() => clearTimeout(copyTimer));

  function signOut() {
    close();
    // Drop the local session first so a hung server call can't keep us signed
    // in; revoke server-side best-effort after.
    clearSession();
    navigate("/login", { replace: true });
    void client.logout().catch(() => {});
  }

  return (
    <div class="shell">
      <header class="topbar">
        <div class="topbar-inner">
          <span class="brand">
            <span class="brand-mark">GG</span> Dashboard
          </span>
          <button
            type="button"
            class="nav-toggle"
            classList={{ "is-open": open() }}
            aria-label={open() ? "Close menu" : "Open menu"}
            aria-expanded={open()}
            aria-controls="topnav"
            onClick={() => setOpen(!open())}
          >
            <span class="nav-toggle-bar" />
            <span class="nav-toggle-bar" />
            <span class="nav-toggle-bar" />
          </button>
          <nav
            id="topnav"
            class="topnav"
            classList={{ "is-open": open() }}
            onKeyDown={(e) => e.key === "Escape" && close()}
          >
            <For each={NAV}>
              {(item) => (
                <A href={item.to} activeClass="active" end onClick={close}>
                  {item.label}
                </A>
              )}
            </For>
          </nav>
          <div class="topbar-right" classList={{ "is-open": open() }}>
            <button
              type="button"
              class="muted account copy-id"
              title={copied() ? "Copied!" : "Click to copy your account id"}
              aria-label="Copy account id"
              onClick={copyAccountId}
            >
              {copied() ? "Copied!" : `${session()?.accountId.slice(0, 8)}…`}
            </button>
            <button type="button" class="ghost" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main class="content">{props.children}</main>
    </div>
  );
}
